import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Visibility, Edit, Add, ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ordersAPI } from "../services/api";
import { Order } from "../types";

const MyOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || ""
  );
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"view" | "edit">("view");

  const { user } = useAuth();
  const navigate = useNavigate();

  const statusColors: {
    [key: string]:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  } = {
    pending_approval: "warning",
    approved: "info",
    rejected: "error",
    preparing: "primary",
    ready_for_shipping: "secondary",
    shipped: "info",
    in_transit: "secondary",
    out_for_delivery: "warning",
    delivered: "success",
    cancelled: "error",
    returned: "error",
    refunded: "error",
  };

  const statusLabels: { [key: string]: string } = {
    pending_approval: "Onay Bekliyor",
    approved: "Onaylandı",
    rejected: "Reddedildi",
    preparing: "Hazırlanıyor",
    ready_for_shipping: "Kargoya Hazır",
    shipped: "Kargolandı",
    in_transit: "Yolda",
    out_for_delivery: "Teslimat İçin Çıktı",
    delivered: "Teslim Edildi",
    cancelled: "İptal Edildi",
    returned: "İade Edildi",
    refunded: "İade Ödendi",
  };

  const fetchOrders = useCallback(async () => {
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        salesperson_id: user?.id, // Only fetch orders for current salesperson
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await ordersAPI.getOrders(params);
      setOrders(response.orders);
      // Ensure pagination is always defined with fallback values
      setPagination(
        response.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  }, [pagination.page, pagination.limit, statusFilter, user?.id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusChange = (event: any) => {
    const newStatus = event.target.value;
    setStatusFilter(newStatus);
    setSearchParams({ status: newStatus });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event: any, value: number) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setDialogType("view");
    setDialogOpen(true);
  };

  const handleEditOrder = (order: Order) => {
    setSelectedOrder(order);
    setDialogType("edit");
    setDialogOpen(true);
  };

  const canEditOrder = (order: Order) => {
    return (
      order.status === "pending_approval" &&
      order.salesperson_id.id === user?.id
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate("/salesperson-dashboard")}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Siparişlerim
          </Typography>
          <Button
            color="inherit"
            startIcon={<Add />}
            onClick={() => navigate("/sales")}
          >
            Yeni Satış
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4">Siparişlerim</Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Durum Filtresi</InputLabel>
            <Select
              value={statusFilter}
              label="Durum Filtresi"
              onChange={handleStatusChange}
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="pending_approval">Bekliyor</MenuItem>
              <MenuItem value="approved">Onaylandı</MenuItem>
              <MenuItem value="rejected">Reddedildi</MenuItem>
              <MenuItem value="ready_for_shipping">Kargoya Hazır</MenuItem>
              <MenuItem value="in_transit">Yolda</MenuItem>
              <MenuItem value="delivered">Teslim Edildi</MenuItem>
              <MenuItem value="cancelled">İptal Edildi</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sipariş No</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell>Tutar</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.order_number}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>₺{order.total_amount}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[order.status]}
                        color={statusColors[order.status] as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(order.created_at).toLocaleDateString("tr-TR")}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewOrder(order)}
                      >
                        <Visibility />
                      </IconButton>
                      {canEditOrder(order) && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditOrder(order)}
                        >
                          <Edit />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Container>

      {/* Order Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "view" ? "Sipariş Detayı" : "Sipariş Düzenle"}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Sipariş No: {selectedOrder.order_number}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Müşteri: {selectedOrder.customer_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Tutar: ₺{selectedOrder.total_amount}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Durum: {statusLabels[selectedOrder.status]}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Tarih:{" "}
                {new Date(selectedOrder.created_at).toLocaleDateString("tr-TR")}
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Ürünler:
              </Typography>
              {selectedOrder.items.map((item, index) => (
                <Box key={index} sx={{ mb: 1, p: 1, bgcolor: "grey.50" }}>
                  <Typography variant="body2">
                    {item.product_name} - {item.quantity} adet - ₺
                    {item.unit_price}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyOrders;
